import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import type { Schemas } from "../workspace/api.generated";
import * as exportApi from "./api";
import type { ExportListCriteria, ExportRequestValues } from "./schema";

const TERMINAL_EXPORT_STATUSES = new Set(["READY", "FAILED"]);

export const exportKeys = {
  all: (epoch: number) => ["admin", epoch, "exports"] as const,
  lists: (epoch: number) => [...exportKeys.all(epoch), "list"] as const,
  list: (epoch: number, criteria: ExportListCriteria) => [...exportKeys.lists(epoch), criteria] as const,
  details: (epoch: number) => [...exportKeys.all(epoch), "detail"] as const,
  detail: (epoch: number, jobId: string) => [...exportKeys.details(epoch), jobId] as const,
};

export function useExportList(criteria: ExportListCriteria) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: exportKeys.list(session.sessionEpoch, criteria),
    queryFn: ({ signal }) => exportApi.getExports(apiClient, criteria, signal),
    staleTime: 0,
  });
}

export function useExportDetail(jobId: string | undefined, poll: boolean) {
  const { apiClient, polling } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: exportKeys.detail(session.sessionEpoch, jobId ?? "none"),
    queryFn: ({ signal }) => exportApi.getExport(apiClient, jobId!, signal),
    enabled: Boolean(jobId) && poll,
    staleTime: 0,
    refetchInterval: (query) => {
      if (!jobId || !poll || query.state.status === "error") return false;
      const current = query.state.data as Schemas["DataExportJobView"] | undefined;
      return current && TERMINAL_EXPORT_STATUSES.has(current.status) ? false : polling.initialDelayMs;
    },
  });
}

export function useRequestExport() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (values: ExportRequestValues) => {
      const request = sessionLifecycle.createRequestController();
      try { return await exportApi.requestSettlementExport(apiClient, values, request.controller.signal); }
      finally { request.release(); }
    },
    onSuccess: async (job) => {
      queryClient.setQueryData(exportKeys.detail(session.sessionEpoch, job.jobId), job);
      await queryClient.invalidateQueries({ queryKey: exportKeys.lists(session.sessionEpoch) });
    },
  });
}
